'use client';

import { type AiResponseReportFileWithUrl } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  LoadingIcon,
  MultipleSelect,
  Skeleton,
  Textarea,
  toast,
} from '@kalos/ui';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from '@radix-ui/react-icons';
import { ImageIcon } from 'lucide-react';
import { useState } from 'react';

import { useS3FileUrlQuery } from '../../../../hooks/react-query/useS3';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { useBuildPdf, type UseBuildPdfArgs } from './useBuildPdf';

type BuildPdfModalProps = UseBuildPdfArgs;

/** One photo to pick for the PDF, loaded from a fresh signed URL (the stored one expires). */
const PdfPhotoTile = ({
  file,
  order,
  onToggle,
}: {
  file: AiResponseReportFileWithUrl;
  /** Its place in the PDF, from 1; 0 when not picked. */
  order: number;
  onToggle: () => void;
}) => {
  const urlQuery = useS3FileUrlQuery({
    filter: { fileName: file.fileName, bucket: file.fileBucket },
    enabled: !!file.fileName && !!file.fileBucket,
  });
  const [hasFailed, setHasFailed] = useState(false);
  const src = urlQuery.data || file.expiringUrl;
  const isSelected = order > 0;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={isSelected}
      aria-label={
        isSelected ? `Photo ${order} in the PDF: ${file.fileName}` : `Add photo ${file.fileName}`
      }
      className={`bg-muted relative flex aspect-square items-center justify-center overflow-hidden rounded-md border-2 ${
        isSelected ? 'border-primary' : 'border-transparent'
      }`}
    >
      {urlQuery.isPending && file.fileBucket ? (
        <Skeleton className="h-full w-full rounded-none" />
      ) : src && !hasFailed ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setHasFailed(true)}
        />
      ) : (
        <ImageIcon className="text-muted-foreground h-6 w-6" aria-hidden />
      )}
      {isSelected && (
        <span className="bg-primary text-primary-foreground absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
          {order}
        </span>
      )}
    </button>
  );
};

export const BuildPdfModal = ({
  aiResponse,
  open,
  onOpenChange,
  onPdfBuilt,
  primaryEventId,
}: BuildPdfModalProps) => {
  const isMobileDevice = useMobileDevice();
  const {
    step,
    setStep,
    selectedFileIds,
    editedText,
    setEditedText,
    textSaveStatus,
    pdfUrl,
    blobPdfUrl,
    isGeneratingPdf,
    pdfGenerationError,
    setPdfGenerationError,
    isProcessingStep1,
    isDownloadCooldown,
    selectedJobNumbers,
    setSelectedJobNumbers,
    jobNumberFieldError,
    jobNumbersInputRef,
    jobNumbersErrorId,
    jobNumberOptions,
    getPdfDownloadFilename,
    isJobNumbersPipelineLoading,
    reportDataQuery,
    batchUpdateReportFilesMutation,
    files,
    isStep2ReportTextReady,
    handleStep1Next,
    handleToggleFile,
    handleDeselectAll,
    handlePersistAndGoToStep3,
    handleDownloadPdf,
    handleClose,
    isLoading,
    selectedCount,
    isPdfPipelineBusy,
    markPdfDownloaded,
  } = useBuildPdf({ aiResponse, open, onOpenChange, onPdfBuilt, primaryEventId });

  // Phones: full-width footer buttons, 44px on touch; desktop keeps its compact ones.
  const footerButtonSize = isMobileDevice ? 'default' : 'sm';
  const footerButtonClass = isMobileDevice ? 'touch:min-h-11 w-full' : 'min-w-[140px]';

  const content = (
    <div
      className={`flex w-full flex-col overflow-hidden ${isMobileDevice ? 'h-dvh' : 'max-h-[90vh]'}`}
    >
      <DialogHeader
        className={
          isMobileDevice
            ? 'bg-background shrink-0 border-b px-4 py-3 text-left'
            : 'sticky top-0 z-10 shrink-0 border-b bg-white px-6 py-4'
        }
      >
        {isMobileDevice ? (
          <div className="flex items-center justify-between gap-2">
            <DialogTitle>Build PDF</DialogTitle>
            <Button variant="ghost" className="-mr-2" onClick={handleClose}>
              Close
            </Button>
          </div>
        ) : (
          <DialogTitle>Build PDF</DialogTitle>
        )}
        <div
          className={`flex flex-wrap items-center gap-2 text-sm ${
            isMobileDevice ? 'text-muted-foreground' : 'mt-2 text-gray-600'
          }`}
        >
          <span>
            Step {step} of 3:{' '}
            {step === 1 ? 'Photos' : step === 2 ? 'Review Report' : 'Download PDF'}
          </span>
          <span>•</span>
          <span>{selectedCount} photo(s) selected</span>
        </div>
      </DialogHeader>

      <div className={`flex-1 overflow-y-auto py-4 ${isMobileDevice ? 'px-4' : 'p-6'}`}>
        {isLoading && !reportDataQuery.data ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <LoadingIcon className="h-10 w-10" />
          </div>
        ) : step === 1 ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Select Photos</h3>
            <p className="!mt-1 text-sm text-gray-600">
              Tap photos in the order you want them in the PDF. First tap = first in PDF.
            </p>
            {files.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                <p className="text-sm text-gray-500">No photos available</p>
                <p className="mt-2 text-sm text-gray-600">
                  You can proceed to the next step to add report text only.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {files.map((file) => (
                  <PdfPhotoTile
                    key={file.id}
                    file={file}
                    order={selectedFileIds.indexOf(file.id ?? 0) + 1}
                    onToggle={() => handleToggleFile(file)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : step === 2 ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Review & Edit Report Summary</h3>
            <p className="!mt-1 text-sm text-gray-600">
              Review and edit the report text. Changes are saved automatically.
            </p>
            {(isJobNumbersPipelineLoading || jobNumberOptions.length > 0) && (
              <div className="space-y-2">
                <label
                  htmlFor="job-numbers-select"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Job Number(s)
                  {!isJobNumbersPipelineLoading && jobNumberOptions.length > 0 ? (
                    <span className="text-destructive" aria-hidden>
                      {' '}
                      *
                    </span>
                  ) : null}
                </label>
                {isJobNumbersPipelineLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full rounded-md" />
                    <p className="text-sm text-gray-500">Loading job numbers…</p>
                  </div>
                ) : (
                  <>
                    <MultipleSelect
                      ref={jobNumbersInputRef}
                      id="job-numbers-select"
                      placeholder="Select job numbers"
                      selected={selectedJobNumbers}
                      onChange={(values) => {
                        const uniqueValues = [...new Set(values)];
                        setSelectedJobNumbers(uniqueValues);
                      }}
                      disabled={isLoading || isJobNumbersPipelineLoading}
                      options={jobNumberOptions}
                      // One preselected job needs no Clear all; its own × removes it.
                      clearAllMinSelections={2}
                      error={!!jobNumberFieldError}
                      aria-required
                      aria-describedby={jobNumberFieldError ? jobNumbersErrorId : undefined}
                    />
                    {jobNumberFieldError ? (
                      <p
                        id={jobNumbersErrorId}
                        role="alert"
                        className="text-destructive text-[0.8rem] font-medium"
                      >
                        {jobNumberFieldError}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            )}
            <div className="relative">
              {!isStep2ReportTextReady ? (
                <div className="space-y-2">
                  <Skeleton className="min-h-[250px] w-full rounded-md md:min-h-[350px]" />
                  <p className="text-sm text-gray-500">Loading saved report…</p>
                </div>
              ) : (
                <Textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="min-h-[250px] font-sans text-sm md:min-h-[350px]"
                  placeholder="Report text..."
                />
              )}
              {isStep2ReportTextReady ? (
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditedText(aiResponse.response ?? '')}
                  >
                    Use original report
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        ) : step === 3 ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">PDF Preview</h3>
            <p className="!mt-1 text-sm text-gray-600">
              Review the final report. Download when ready.
            </p>
            {pdfGenerationError ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-6">
                <p className="text-sm font-medium text-red-800">PDF generation failed</p>
                <p className="text-sm text-red-600">{pdfGenerationError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPdfGenerationError(null);
                    handlePersistAndGoToStep3();
                  }}
                >
                  Try again
                </Button>
              </div>
            ) : isPdfPipelineBusy ? (
              <div className="relative">
                <Skeleton className="h-[400px] w-full animate-pulse rounded-lg" />
                <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-gray-600">
                  <LoadingIcon className="h-4 w-4" />
                  {isGeneratingPdf ? 'Generating PDF...' : 'Loading PDF preview...'}
                </div>
              </div>
            ) : blobPdfUrl && isMobileDevice ? (
              // Phone browsers don't reliably show a PDF inline; the download opens it instead.
              <div className="border-border flex flex-col gap-1 rounded-md border p-4">
                <p className="text-sm font-semibold">Your PDF is ready</p>
                <p className="text-muted-foreground break-all text-sm">
                  {getPdfDownloadFilename()}
                </p>
                <p className="text-muted-foreground text-sm">
                  {selectedCount} photo{selectedCount === 1 ? '' : 's'} and the report text.
                </p>
              </div>
            ) : blobPdfUrl ? (
              <iframe
                src={`${blobPdfUrl}#toolbar=0&navpanes=0`}
                title="PDF Preview"
                className="h-[400px] w-full rounded-lg border"
              />
            ) : (
              <div className="flex min-h-[200px] items-center justify-center">
                <p className="text-sm text-gray-500">No PDF to preview</p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div
        className={
          isMobileDevice
            ? 'bg-background shrink-0 border-t px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3'
            : 'sticky bottom-0 shrink-0 border-t bg-white px-6 py-4'
        }
      >
        <div
          className={`flex flex-wrap items-center gap-2 ${
            step === 2 ? 'justify-between' : 'justify-end'
          }`}
        >
          {step === 2 ? (
            <div
              className="min-h-5 text-sm text-gray-500"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {isStep2ReportTextReady && textSaveStatus === 'saving' ? (
                <span className="flex items-center gap-1">
                  <LoadingIcon className="h-4 w-4 shrink-0" />
                  Saving...
                </span>
              ) : null}
              {isStep2ReportTextReady && textSaveStatus === 'saved' ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckIcon className="h-4 w-4 shrink-0" />
                  Saved
                </span>
              ) : null}
            </div>
          ) : null}
          <div
            className={
              isMobileDevice
                ? 'grid w-full grid-cols-2 gap-2'
                : 'flex flex-wrap items-center justify-end gap-2'
            }
          >
            {step === 1 && (
              <>
                <Button
                  variant="outline"
                  size={footerButtonSize}
                  className={footerButtonClass}
                  onClick={handleDeselectAll}
                  disabled={
                    selectedFileIds.length === 0 ||
                    isProcessingStep1 ||
                    isLoading ||
                    batchUpdateReportFilesMutation.isPending
                  }
                >
                  {isMobileDevice ? 'Clear' : 'Deselect all'}
                </Button>
                <Button
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={handleStep1Next}
                  disabled={
                    isProcessingStep1 || isLoading || batchUpdateReportFilesMutation.isPending
                  }
                >
                  {isMobileDevice ? 'Next' : 'Next: Review Report'}
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button
                  variant="outline"
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={() => setStep(1)}
                  disabled={isPdfPipelineBusy}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  {isMobileDevice ? 'Back' : 'Back: Photos'}
                </Button>
                <Button
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={handlePersistAndGoToStep3}
                  disabled={isPdfPipelineBusy || isJobNumbersPipelineLoading}
                  isLoading={isPdfPipelineBusy}
                >
                  {isMobileDevice ? 'Next' : 'Next: Download PDF'}
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            {step === 3 && (
              <>
                <Button
                  variant="outline"
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={() => setStep(2)}
                  disabled={isPdfPipelineBusy}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  {isMobileDevice ? 'Back' : 'Back: Review Report'}
                </Button>
                {isPdfPipelineBusy ? (
                  <Button
                    variant="outline"
                    size={footerButtonSize}
                    disabled
                    className={`gap-2 ${footerButtonClass}`}
                  >
                    <LoadingIcon className="h-4 w-4" />
                    Preparing PDF…
                  </Button>
                ) : blobPdfUrl ? (
                  isDownloadCooldown ? (
                    <Button
                      variant="muted"
                      size={footerButtonSize}
                      disabled
                      className={`gap-2 ${footerButtonClass}`}
                    >
                      <CheckIcon className="h-4 w-4" />
                      Downloaded
                    </Button>
                  ) : (
                    <Button
                      size={footerButtonSize}
                      className={`gap-2 ${footerButtonClass}`}
                      onClick={() => {
                        if (!blobPdfUrl) return;
                        const filename = getPdfDownloadFilename();
                        const a = document.createElement('a');
                        a.href = blobPdfUrl;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        markPdfDownloaded();
                        toast({
                          title: 'PDF Downloaded',
                          description: 'Check your downloads folder.',
                          variant: 'success',
                        });
                      }}
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Download PDF
                    </Button>
                  )
                ) : (
                  <Button
                    size={footerButtonSize}
                    className={`gap-2 ${footerButtonClass}`}
                    onClick={handleDownloadPdf}
                    disabled={!pdfUrl || isPdfPipelineBusy}
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Download PDF
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent
        className={
          isMobileDevice
            ? 'bg-background left-0 top-0 h-dvh w-full max-w-none translate-x-0 translate-y-0 overflow-hidden rounded-none border-0 p-0 sm:rounded-none'
            : 'max-h-[90vh] max-w-2xl max-w-[800px] overflow-hidden bg-white p-0'
        }
        showClose={!isMobileDevice}
        // Full screen on phones: it must not re-center itself for the keyboard.
        keepFocusVisibleAboveKeyboard={!isMobileDevice}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
};
